import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Toolbar,
} from '@mui/material';
import { useAuth } from '../hooks/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Topbar from '../components/Layout/Topbar';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If user was redirected from a protected route,
  // React Router v6 state will look like: { from: locationObj }
  const fromLocation = location.state?.from;
  const redirectTo =
    (fromLocation && fromLocation.pathname) ? fromLocation.pathname : '/';

  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));

    // Clear per-field error when user edits it
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });

    setGlobalError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFieldErrors({});
    setGlobalError('');

    try {
      await login(form.email, form.password);
      // On successful login, go back to where they came from, or home
      navigate(redirectTo, { replace: true });
    } catch (err) {
      // err is thrown by AuthContext.login → requestJson
      if (err.status === 400 && err.validationErrors) {
        // Backend returned { field: "message" } validation errors
        setFieldErrors(err.validationErrors);
      } else if (err.status === 401 || err.status === 403) {
        setGlobalError('Invalid email or password.');
      } else if (err.status === 0) {
        setGlobalError('Network error. Please check your connection.');
      } else {
        setGlobalError('Something went wrong while logging in.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: '100%',
        minHeight: '100dvh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Topbar />
      <Toolbar />

      <Paper
        elevation={2}
        sx={{
          maxWidth: 420,
          p: 4,
          borderRadius: 3,
        }}
      >
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Stargate Rentals Admin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to manage vehicles, drivers and hire agreements.
          </Typography>
        </Box>

        {globalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {globalError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange('email')}
            fullWidth
            margin="normal"
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />

          <TextField
            label="Password"
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange('password')}
            fullWidth
            margin="normal"
            error={!!fieldErrors.password}
            helperText={fieldErrors.password}
          />

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="secondary" // uses your pill/gradient style
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={22} sx={{ color: 'common.white' }} />
              ) : (
                'Sign in'
              )}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Having trouble logging in? Contact the system owner.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
